import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  CAlert,
  CBadge,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CMultiSelect,
  CSpinner,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilBook, cilChartLine, cilEqualizer } from "@coreui/icons";
import {
  CommunicationCategory,
  CommunicationChannel,
  useCommunicationPreferences,
} from "../../api/hooks/useCommunicationPreferences";
import { usePortfolioTrackerEnabled } from "../../api/hooks/usePortalSettings";
import {
  useTradingStrategySignalFeed,
} from "../../api/hooks/useSignals";
import { useTradingStrategies } from "../../api/hooks/useTradingStrategies";
import { helpArticlePath, routePaths } from "../../routes/route-paths";
import { StrategySideLabel } from "../trading-strategies/components/StrategySideLabel";
import { displayTicker } from "../trading-strategies/tickers";
import { SignalFeed } from "./components/SignalFeed";
import { StrategyInstruments } from "./components/StrategyInstruments";
import { StrategyStanding } from "./components/StrategyStanding";

const EMAIL_PROMPT_DISMISSED_KEY = "scryer-signals-email-prompt-dismissed";

const readEmailPromptDismissed = (): boolean => {
  try {
    return localStorage.getItem(EMAIL_PROMPT_DISMISSED_KEY) === "1";
  } catch {
    // Private windows and blocked site data throw on access. Showing the prompt again is harmless.
    return false;
  }
};

/**
 * The customer's signals: where each strategy stands now, then every signal by day, newest first, filtered by
 * strategy and a page at a time.
 *
 * Who receives what is the API's decision: it returns the strategies the customer receives and their signals.
 */
export function Signals() {
  const { t } = useTranslation(["signals", "tradingStrategies", "common"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const preferences = useCommunicationPreferences();
  const trackerEnabled = usePortfolioTrackerEnabled();
  const [emailPromptDismissed, setEmailPromptDismissed] = useState(readEmailPromptDismissed);

  const strategyId = searchParams.get("strategy");
  const followingOnly = searchParams.get("view") !== "all";
  const feed = useTradingStrategySignalFeed({ strategyId, following: followingOnly });

  const tradingStrategies = useTradingStrategies();
  const strategyDetails = useMemo(
    () => new Map((tradingStrategies.data ?? []).map((strategy) => [strategy.id, strategy])),
    [tradingStrategies.data],
  );

  const firstPage = feed.data?.pages[0];
  const allStrategies = firstPage?.strategies ?? [];
  const followingCount = allStrategies.filter((s) => s.following).length;
  // Someone who follows everything they can access, as anyone who only bought strategies does, has
  // nothing for the filter to narrow.
  const showFollowFilter = followingCount < allStrategies.length;
  const strategies = followingOnly ? allStrategies.filter((s) => s.following) : allStrategies;
  // The cards are a quick view of what the customer trades, so they stay on what they follow
  // whichever list is showing below.
  const followedStrategies = allStrategies.filter((s) => s.following);
  const followingNothing = followingOnly && allStrategies.length > 0 && followingCount === 0;
  const totalCount = firstPage?.totalCount ?? 0;
  const signals = feed.data?.pages.flatMap((page) => page.signals) ?? [];
  const selectedStrategy = strategies.find((s) => s.id === strategyId) ?? null;
  const filtered = strategyId !== null;

  // Two versions of a strategy share its name, so each option says what it charts, what it trades and
  // which way. The search matches this text, so any of those finds it.
  const strategyOption = (strategy: { id: string; name: string }) => {
    const detail = strategyDetails.get(strategy.id);
    if (!detail) {
      return strategy.name;
    }

    const charted = displayTicker(detail.chartedSymbol);
    const traded = [detail.bullSymbol, detail.bearSymbol].map(displayTicker).filter(Boolean).join(" / ");
    const instruments = traded ? `${charted} → ${traded}` : charted;
    const side = detail.direction ? t(`tradingStrategies:sides.${detail.direction}`) : null;

    return [strategy.name, side, instruments].filter(Boolean).join(" · ");
  };

  const signalEmail = preferences.data?.find(
    (p) => p.category === CommunicationCategory.Signals && p.channel === CommunicationChannel.Email,
  );
  const signalEmailsOff = signalEmail !== undefined && !signalEmail.allowed;

  const holdsNothing = allStrategies.length === 0;

  const setFilter = (name: "strategy" | "view", value: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (value === null || value === "") {
      params.delete(name);
    } else {
      params.set(name, value);
    }
    setSearchParams(params);
  };

  const dismissEmailPrompt = () => {
    try {
      localStorage.setItem(EMAIL_PROMPT_DISMISSED_KEY, "1");
    } catch {
      // Nothing to do - it stays hidden until the page is opened again.
    }
    setEmailPromptDismissed(true);
  };

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("strategy");
    setSearchParams(params);
  };

  return (
    <div>
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-start gap-2 mb-4">
        <div>
          <h1 className="mb-1">{t("page.title")}</h1>
          <p className="text-body-secondary mb-0">{t("page.description")}</p>
        </div>
        {/* Full width on a phone, where they otherwise sit small under the heading. */}
        <div className="col-12 col-sm-auto d-flex flex-column flex-sm-row gap-2">
          <Link to={helpArticlePath("reading-your-signals")} className="btn btn-outline-secondary">
            <CIcon icon={cilBook} className="me-2" />
            {t("page.help")}
          </Link>
          {trackerEnabled && allStrategies.length > 0 && (
            <Link to={routePaths.portfolioTracker} className="btn btn-primary">
              <CIcon icon={cilChartLine} className="me-2" />
              {t("page.trackPortfolio")}
              <CBadge color="light" textColor="primary" shape="rounded-pill" className="ms-2">
                {t("common:navigationBadges.new")}
              </CBadge>
            </Link>
          )}
          {allStrategies.length > 0 && (
            <Link to={routePaths.signalsConfigure} className="btn btn-primary">
              <CIcon icon={cilEqualizer} className="me-2" />
              {t("page.configure")}
            </Link>
          )}
        </div>
      </div>

      {signalEmailsOff && !holdsNothing && !emailPromptDismissed && (
        <CAlert color="info" dismissible onClose={dismissEmailPrompt}>
          <Trans
            t={t}
            i18nKey="page.emailsOffPrompt"
            components={{ turnOn: <Link to={routePaths.profileCommunications} /> }}
          />
        </CAlert>
      )}

      {feed.isLoading && (
        <div className="d-flex justify-content-center py-5">
          <CSpinner color="primary" />
        </div>
      )}

      {feed.isError && <CAlert color="danger">{t("page.loadError")}</CAlert>}

      {feed.isSuccess && holdsNothing && (
        <EmptyState
          title={t("empty.noStrategyTitle")}
          body={t("empty.noStrategyBody")}
          action={t("empty.browseStrategies")}
          to={routePaths.strategies}
        />
      )}

      {feed.isSuccess && followingNothing && (
        <EmptyState
          title={t("empty.notFollowingTitle")}
          body={t("empty.notFollowingBody", { count: allStrategies.length })}
          action={t("empty.configure")}
          to={routePaths.signalsConfigure}
          secondary={{ label: t("empty.seeEverything"), onClick: () => setFilter("view", "all") }}
        />
      )}

      {feed.isSuccess && strategies.length > 0 && (
        <>
          {followedStrategies.length > 0 && (
            <StrategyStanding
              strategies={followedStrategies}
              details={strategyDetails}
              selectedId={selectedStrategy?.id ?? null}
              onSelect={(id) => setFilter("strategy", id === selectedStrategy?.id ? null : id)}
            />
          )}

          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            {strategies.length > 1 && (
              <div style={{ flex: "1 1 18rem", maxWidth: "26rem" }}>
                <label htmlFor="signals-strategy-filter" className="visually-hidden">
                  {t("page.strategyFilter")}
                </label>
                <CMultiSelect
                  id="signals-strategy-filter"
                  options={strategies.map((strategy) => ({ value: strategy.id, label: strategyOption(strategy) }))}
                  value={selectedStrategy ? selectedStrategy.id : []}
                  multiple={false}
                  optionsStyle="text"
                  // Not optionsMaxHeight: CoreUI pairs that with overflow: scroll, which draws a horizontal
                  // scrollbar as well. The scroller only scrolls vertically.
                  virtualScroller
                  visibleItems={5}
                  search
                  cleaner
                  placeholder={t("page.allStrategies")}
                  searchNoResultsLabel={t("page.noStrategyMatches")}
                  optionsTemplate={(option) => {
                    const id = option.value.toString();
                    const detail = strategyDetails.get(id);
                    const name = strategies.find((strategy) => strategy.id === id)?.name ?? option.label;

                    return (
                      <span className="d-flex justify-content-between align-items-center gap-3 w-100">
                        <span className="d-flex flex-column">
                          <span className="fw-semibold">{name}</span>
                          {detail && <StrategyInstruments strategy={detail} compact />}
                        </span>
                        <StrategySideLabel direction={detail?.direction} className="small" />
                      </span>
                    );
                  }}
                  onChange={(chosen) => {
                    const value = chosen[0]?.value.toString() ?? null;
                    // CoreUI raises onChange while it settles on mount as well as on a real choice; writing
                    // that to the URL would clear a filter the customer arrived with.
                    if (value !== (selectedStrategy?.id ?? null)) {
                      setFilter("strategy", value);
                    }
                  }}
                />
              </div>
            )}
            {showFollowFilter && (
              <CButtonGroup role="group" aria-label={t("follow.filter")}>
                <CButton
                  color="primary"
                  variant={followingOnly ? undefined : "outline"}
                  aria-pressed={followingOnly}
                  onClick={() => setFilter("view", null)}
                >
                  {t("follow.following", { count: followingCount })}
                </CButton>
                <CButton
                  color="primary"
                  variant={followingOnly ? "outline" : undefined}
                  aria-pressed={!followingOnly}
                  onClick={() => setFilter("view", "all")}
                >
                  {t("follow.everything", { count: allStrategies.length })}
                </CButton>
              </CButtonGroup>
            )}
            <span className="small text-body-secondary ms-auto">{t("feed.count", { count: totalCount })}</span>
          </div>

          {signals.length === 0 ? (
            <CAlert color="info">
              {!filtered && t("page.noSignals")}
              {filtered && selectedStrategy !== null &&
                t("page.noSignalsFromStrategy", { strategy: selectedStrategy.name })}
              {filtered && selectedStrategy === null && (
                <>
                  {t("page.noMatches")}{" "}
                  <CButton color="link" className="p-0 align-baseline" onClick={clearFilters}>
                    {t("page.clearFilters")}
                  </CButton>
                </>
              )}
            </CAlert>
          ) : (
            <SignalFeed signals={signals} />
          )}

          {feed.hasNextPage && (
            <div className="d-flex justify-content-center mt-3">
              <CButton
                color="secondary"
                variant="outline"
                disabled={feed.isFetchingNextPage}
                onClick={() => feed.fetchNextPage()}
              >
                {feed.isFetchingNextPage && <CSpinner size="sm" className="me-2" />}
                {t("feed.showOlder")}
              </CButton>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EmptyState({
  title,
  body,
  action,
  to,
  secondary,
}: {
  title: string;
  body: string;
  action: string;
  to: string;
  secondary?: { label: string; onClick: () => void };
}) {
  return (
    <CCard>
      <CCardBody className="text-center py-5">
        <h2 className="h5 mb-2">{title}</h2>
        <p className="text-body-secondary mx-auto mb-4" style={{ maxWidth: 480 }}>
          {body}
        </p>
        <div className="d-flex justify-content-center flex-wrap gap-2">
          <Link to={to} className="btn btn-primary">
            {action}
          </Link>
          {secondary && (
            <CButton color="secondary" variant="outline" onClick={secondary.onClick}>
              {secondary.label}
            </CButton>
          )}
        </div>
      </CCardBody>
    </CCard>
  );
}

export default Signals;
